
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export function useSetupGuide() {
  const { user, isAuthenticated } = useAuth();
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notificationDismissed, setNotificationDismissed] = useState(false);

  useEffect(() => {
    const checkSetupStatus = async () => {
      if (!isAuthenticated || !user) {
        setLoading(false);
        return;
      }

      try {
        // Check if user has completed setup
        const { data, error } = await supabase
          .from("user_preferences")
          .select("setup_completed")
          .eq("user_id", user.id)
          .single();

        if (error) {
          console.error("Error checking setup status:", error);
          setLoading(false);
          return;
        }

        // If setup_completed is false or null, show the setup guide
        setShowSetupGuide(data?.setup_completed !== true);
        setLoading(false);
      } catch (error) {
        console.error("Error in setup guide check:", error);
        setLoading(false);
      }
    };

    checkSetupStatus();
    
    // Reset notification dismissed state when user logs in
    setNotificationDismissed(false);
  }, [isAuthenticated, user]);

  const markSetupComplete = async () => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from("user_preferences")
        .update({ setup_completed: true })
        .eq("user_id", user.id);

      if (error) {
        console.error("Error marking setup as complete:", error);
        return false;
      }

      setShowSetupGuide(false);
      return true;
    } catch (error) {
      console.error("Error in marking setup complete:", error);
      return false;
    }
  };

  const dismissNotification = () => {
    setNotificationDismissed(true);
  };

  const permanentlyDismissSetupGuide = async () => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from("user_preferences")
        .update({ setup_completed: true })
        .eq("user_id", user.id);

      if (error) {
        console.error("Error permanently dismissing setup guide:", error);
        return false;
      }

      setShowSetupGuide(false);
      return true;
    } catch (error) {
      console.error("Error in permanently dismissing setup guide:", error);
      return false;
    }
  };

  const openSetupDialog = () => {
    setDialogOpen(true);
  };

  return {
    showSetupGuide,
    setShowSetupGuide,
    markSetupComplete,
    loading,
    notificationDismissed,
    dismissNotification,
    permanentlyDismissSetupGuide,
    dialogOpen,
    setDialogOpen,
    openSetupDialog
  };
}
